import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import {
  Tooltip,
  Typography,
} from '@mui/material';

type ClipToolTipProps = {
  title: string;
  children?: React.ReactNode;
  variant?: React.ComponentProps<typeof Typography>['variant'];
  sx?: React.ComponentProps<typeof Typography>['sx'];
};

export function ClipToolTip({
  title,
  children,
  variant = 'h6',
  sx,
}: ClipToolTipProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const check = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);
  useLayoutEffect(() => {
    check();
  }, [title, check]);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resizeObs = new ResizeObserver(() => check());
    resizeObs.observe(element);
    return () => resizeObs.disconnect();
  }, [check]);

  return (
    <Tooltip
      title={title}
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
      placement="top-end"
    >
      <Typography
        ref={ref}
        component="span"
        variant={variant}
        noWrap
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...sx,
        }}
      >
        {children ?? title}
      </Typography>
    </Tooltip>
  )
}